import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle,
  Clock,
  Filter,
  Search,
  Bell,
  BellOff,
  Zap,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Cpu,
  HardDrive,
  Activity,
  Target,
  RefreshCw,
  ExternalLink,
  X
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

import { parseUtc } from '../lib/parseUtc';
const RealTimeAlertsDashboard = ({ clusterId }) => {
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [alertStats, setAlertStats] = useState({});
  const [alertHistory, setAlertHistory] = useState([]);
  const [mutedAlerts, setMutedAlerts] = useState(new Set());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (clusterId) {
      fetchAlerts();
      // Set up real-time polling every 15 seconds for alerts
      intervalRef.current = setInterval(fetchAlerts, 15000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clusterId]);

  useEffect(() => {
    applyFilters();
  }, [alerts, searchTerm, severityFilter, statusFilter, mutedAlerts]);

  const fetchAlerts = async () => {
    try {
      const [realTimeRes, alertHistoryRes] = await Promise.all([
        axios.get(`${API}/realtime-metrics/${clusterId}`),
        axios.get(`${API}/metrics/${clusterId}?limit=24`) // Last 24 hours for trend analysis
      ]);

      const realTimeData = realTimeRes.data;
      const historicalData = alertHistoryRes.data || [];

      // Generate comprehensive alerts from real-time data
      const generatedAlerts = generateAlertsFromMetrics(realTimeData, historicalData);
      
      setAlerts(generatedAlerts);
      setAlertStats(calculateAlertStats(generatedAlerts));
      setAlertHistory(processAlertHistory(historicalData));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch alerts');
      toast.error('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const generateAlertsFromMetrics = (realTimeData, historicalData) => {
    const alerts = [];
    const timestamp = new Date().toISOString();

    // Cost-based alerts
    if (realTimeData.cost_per_hour > 10) {
      alerts.push({
        id: `cost-high-${Date.now()}`,
        type: 'cost',
        severity: realTimeData.cost_per_hour > 50 ? 'critical' : 'warning',
        title: 'High Cost Alert',
        message: `Cluster cost is ${realTimeData.cost_per_hour.toFixed(2)}/hour, exceeding normal thresholds`,
        timestamp,
        status: 'active',
        source: 'cost-monitor',
        recommendations: [
          'Review resource requests and limits',
          'Consider scaling down underutilized pods',
          'Implement resource quotas',
          'Enable cluster autoscaling'
        ],
        metrics: {
          current_value: realTimeData.cost_per_hour,
          threshold: 10,
          trend: calculateTrend(historicalData, 'cost_estimate')
        }
      });
    }

    // Efficiency-based alerts
    if (realTimeData.efficiency_score < 60) {
      alerts.push({
        id: `efficiency-low-${Date.now()}`,
        type: 'efficiency',
        severity: realTimeData.efficiency_score < 30 ? 'critical' : 'warning',
        title: 'Low Efficiency Alert',
        message: `Cluster efficiency is ${realTimeData.efficiency_score.toFixed(1)}%, indicating resource waste`,
        timestamp,
        status: 'active',
        source: 'efficiency-monitor',
        recommendations: [
          'Optimize resource requests to match actual usage',
          'Remove unused or idle pods',
          'Implement horizontal pod autoscaling',
          'Review and adjust resource limits'
        ],
        metrics: {
          current_value: realTimeData.efficiency_score,
          threshold: 60,
          trend: 'declining'
        }
      });
    }

    // Resource utilization alerts
    if (realTimeData.active_resources) {
      const { cpu_utilization, memory_utilization } = realTimeData.active_resources;
      
      if (cpu_utilization > 85) {
        alerts.push({
          id: `cpu-high-${Date.now()}`,
          type: 'resource',
          severity: cpu_utilization > 95 ? 'critical' : 'warning',
          title: 'High CPU Utilization',
          message: `CPU utilization is ${cpu_utilization.toFixed(1)}%, approaching capacity limits`,
          timestamp,
          status: 'active',
          source: 'resource-monitor',
          recommendations: [
            'Scale up cluster nodes or pods',
            'Optimize CPU-intensive workloads',
            'Implement CPU limits and requests',
            'Consider vertical pod autoscaling'
          ],
          metrics: {
            current_value: cpu_utilization,
            threshold: 85,
            trend: 'increasing'
          }
        });
      }

      if (memory_utilization > 85) {
        alerts.push({
          id: `memory-high-${Date.now()}`,
          type: 'resource',
          severity: memory_utilization > 95 ? 'critical' : 'warning',
          title: 'High Memory Utilization',
          message: `Memory utilization is ${memory_utilization.toFixed(1)}%, approaching capacity limits`,
          timestamp,
          status: 'active',
          source: 'resource-monitor',
          recommendations: [
            'Scale up cluster nodes or increase memory limits',
            'Optimize memory usage in applications',
            'Implement memory limits and requests',
            'Check for memory leaks in applications'
          ],
          metrics: {
            current_value: memory_utilization,
            threshold: 85,
            trend: 'increasing'
          }
        });
      }
    }

    // Alert volume alerts
    if (realTimeData.active_alerts && realTimeData.active_alerts.length > 10) {
      alerts.push({
        id: `alert-volume-${Date.now()}`,
        type: 'system',
        severity: 'warning',
        title: 'High Alert Volume',
        message: `${realTimeData.active_alerts.length} active alerts detected, indicating potential system issues`,
        timestamp,
        status: 'active',
        source: 'alert-monitor',
        recommendations: [
          'Review and resolve existing alerts',
          'Check for cascading failures',
          'Implement alert grouping and deduplication',
          'Review alert thresholds and sensitivity'
        ],
        metrics: {
          current_value: realTimeData.active_alerts.length,
          threshold: 10,
          trend: 'stable'
        }
      });
    }

    // Add existing alerts from real-time data
    if (realTimeData.active_alerts) {
      realTimeData.active_alerts.forEach((alert, index) => {
        alerts.push({
          id: `existing-${index}-${Date.now()}`,
          type: alert.type || 'general',
          severity: alert.severity || 'info',
          title: alert.message || 'System Alert',
          message: alert.details || alert.message || 'No additional details available',
          timestamp: alert.timestamp || timestamp,
          status: 'active',
          source: alert.source || 'kubernetes',
          recommendations: generateRecommendationsForAlert(alert),
          metrics: alert.metrics || {}
        });
      });
    }

    return alerts;
  };

  const generateRecommendationsForAlert = (alert) => {
    const recommendations = [];
    
    if (alert.type === 'pod') {
      recommendations.push(
        'Check pod logs for errors',
        'Verify resource requests and limits',
        'Review pod configuration',
        'Check node capacity and health'
      );
    } else if (alert.type === 'node') {
      recommendations.push(
        'Check node resource utilization',
        'Verify node health and status',
        'Review node configuration',
        'Consider node scaling or replacement'
      );
    } else {
      recommendations.push(
        'Review system logs',
        'Check resource utilization',
        'Verify configuration settings',
        'Monitor for related issues'
      );
    }
    
    return recommendations;
  };

  const calculateTrend = (historicalData, metric) => {
    if (!historicalData || historicalData.length < 2) return 'stable';
    
    const recent = historicalData.slice(0, 6); // Last 6 data points
    const older = historicalData.slice(6, 12); // Previous 6 data points
    
    const recentAvg = recent.reduce((sum, item) => sum + (item[metric] || 0), 0) / recent.length;
    const olderAvg = older.reduce((sum, item) => sum + (item[metric] || 0), 0) / older.length;
    
    if (recentAvg > olderAvg * 1.1) return 'increasing';
    if (recentAvg < olderAvg * 0.9) return 'decreasing';
    return 'stable';
  };

  const calculateAlertStats = (alerts) => {
    const stats = {
      total: alerts.length,
      critical: alerts.filter(a => a.severity === 'critical').length,
      warning: alerts.filter(a => a.severity === 'warning').length,
      info: alerts.filter(a => a.severity === 'info').length,
      active: alerts.filter(a => a.status === 'active').length,
      resolved: alerts.filter(a => a.status === 'resolved').length,
      by_type: {}
    };

    alerts.forEach(alert => {
      stats.by_type[alert.type] = (stats.by_type[alert.type] || 0) + 1;
    });

    return stats;
  };

  const processAlertHistory = (historicalData) => {
    // Process historical data to show alert trends over time
    return historicalData.map((item, index) => ({
      timestamp: item.timestamp,
      alert_count: Math.floor(Math.random() * 10), // Placeholder - would be calculated from actual alert data
      cost_alerts: Math.floor(Math.random() * 3),
      resource_alerts: Math.floor(Math.random() * 4),
      efficiency_alerts: Math.floor(Math.random() * 2)
    })).reverse();
  };

  const applyFilters = () => {
    let filtered = alerts.filter(alert => !mutedAlerts.has(alert.id));

    if (searchTerm) {
      filtered = filtered.filter(alert =>
        alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (severityFilter !== 'all') {
      filtered = filtered.filter(alert => alert.severity === severityFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(alert => alert.status === statusFilter);
    }

    setFilteredAlerts(filtered);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      case 'warning': return <AlertCircle className="w-4 h-4" />;
      case 'info': return <Info className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cost': return <DollarSign className="w-4 h-4" />;
      case 'resource': return <Cpu className="w-4 h-4" />;
      case 'efficiency': return <Target className="w-4 h-4" />;
      case 'system': return <Activity className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUp className="w-4 h-4 text-red-500" />;
      case 'decreasing': return <TrendingDown className="w-4 h-4 text-green-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const muteAlert = (alertId) => {
    setMutedAlerts(prev => new Set([...prev, alertId]));
    toast.success('Alert muted');
  };

  const unmuteAlert = (alertId) => {
    setMutedAlerts(prev => {
      const newSet = new Set(prev);
      newSet.delete(alertId);
      return newSet;
    });
    toast.success('Alert unmuted');
  };

  const resolveAlert = (alertId) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, status: 'resolved' } : alert
    ));
    toast.success('Alert resolved');
  };

  const dismissAlert = (alertId) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    toast.success('Alert dismissed');
  };

  const CHART_COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981'];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alert Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="stat-card metric-card border-0 shadow-lg hover-lift dark:bg-surface-dark dark:border dark:border-surface-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Total Alerts</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                  {alertStats.total || 0}
                </p>
                <p className="text-xs text-slate-500 dark:text-text-secondary mt-2">{alertStats.active || 0} active</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg">
                <Bell className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card metric-card border-0 shadow-lg hover-lift dark:bg-surface-dark dark:border dark:border-surface-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Critical</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-400 bg-clip-text text-transparent">
                  {alertStats.critical || 0}
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">Immediate attention</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-lg animate-bounce-subtle">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card metric-card border-0 shadow-lg hover-lift dark:bg-surface-dark dark:border dark:border-surface-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Warning</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-amber-500 bg-clip-text text-transparent">
                  {alertStats.warning || 0}
                </p>
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 font-medium">Needs monitoring</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl shadow-lg">
                <AlertCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card metric-card border-0 shadow-lg hover-lift dark:bg-surface-dark dark:border dark:border-surface-border">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-600 dark:text-text-secondary mb-3">Resolved</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent">
                  {alertStats.resolved || 0}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">Recently resolved</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-white dark:bg-surface-dark rounded-xl shadow-sm border border-slate-200 dark:border-surface-border p-2">
          <TabsTrigger value="active" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md dark:text-text-secondary">
            Active Alerts ({filteredAlerts.filter(a => a.status === 'active').length})
          </TabsTrigger>
          <TabsTrigger value="all" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md dark:text-text-secondary">
            All Alerts ({filteredAlerts.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md dark:text-text-secondary">
            Analytics
          </TabsTrigger>
          <TabsTrigger value="muted" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md dark:text-text-secondary">
            Muted ({mutedAlerts.size})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {/* Filters */}
          <Card className="border-0 shadow-lg dark:bg-surface-dark dark:border dark:border-surface-border">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <Input
                      placeholder="Search alerts by title, message, or type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-slate-300 dark:border-surface-border dark:bg-background-dark dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl"
                    />
                  </div>
                </div>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="px-4 py-2.5 border border-slate-300 dark:border-surface-border dark:bg-surface-dark dark:text-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-blue-300 transition-all"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </select>
                <Button
                  onClick={fetchAlerts}
                  className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Active Alerts List */}
          <div className="space-y-4">
            {filteredAlerts.filter(alert => alert.status === 'active').map((alert) => (
              <Card key={alert.id} className="hover:shadow-xl transition-all duration-300 border-l-4 relative group" style={{
                borderLeftColor: alert.severity === 'critical' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6'
              }}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={`p-3 rounded-xl shadow-sm ${getSeverityColor(alert.severity)}`}>
                        {getSeverityIcon(alert.severity)}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg flex items-center space-x-2">
                          <span>{alert.title}</span>
                          <span className="opacity-60">{getTypeIcon(alert.type)}</span>
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{parseUtc(alert.timestamp).toLocaleString()}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Activity className="w-3 h-3" />
                            <span>{alert.source}</span>
                          </span>
                          {alert.metrics?.trend && (
                            <span className="flex items-center space-x-1">
                              {getTrendIcon(alert.metrics.trend)}
                              <span className="capitalize">{alert.metrics.trend}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Badge className={`${getSeverityColor(alert.severity)} shadow-sm`}>
                        {alert.severity}
                      </Badge>
                      <Button
                        onClick={() => muteAlert(alert.id)}
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        title="Mute alert"
                      >
                        <BellOff className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => resolveAlert(alert.id)}
                        variant="ghost"
                        size="sm"
                        className="text-green-500 hover:text-green-700 hover:bg-green-50"
                        title="Resolve alert"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => dismissAlert(alert.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        title="Dismiss alert"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Metrics */}
                  {alert.metrics && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600">Current Value</p>
                        <p className="text-lg font-bold text-red-600">
                          {typeof alert.metrics.current_value === 'number' 
                            ? alert.metrics.current_value.toFixed(2) 
                            : alert.metrics.current_value}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600">Threshold</p>
                        <p className="text-lg font-bold text-gray-800">
                          {alert.metrics.threshold}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-600">Trend</p>
                        <p className="text-lg font-bold flex items-center justify-center space-x-1">
                          {getTrendIcon(alert.metrics.trend)}
                          <span className="capitalize">{alert.metrics.trend}</span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {alert.recommendations && alert.recommendations.length > 0 && (
                    <div className="border-t pt-4">
                      <h5 className="font-medium mb-2 flex items-center">
                        <Target className="w-4 h-4 mr-2 text-blue-600" />
                        Recommended Actions
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {alert.recommendations.map((rec, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm p-2 bg-blue-50 rounded">
                            <Zap className="w-3 h-3 text-blue-600 flex-shrink-0" />
                            <span>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {filteredAlerts.filter(alert => alert.status === 'active').length === 0 && (
              <Card>
                <CardContent className="py-16">
                  <div className="text-center">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Active Alerts
                    </h3>
                    <p className="text-gray-600">
                      All systems are operating normally
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          <div className="space-y-4">
            {filteredAlerts.map((alert) => (
              <Card key={alert.id} className={`hover:shadow-lg transition-shadow ${
                alert.status === 'resolved' ? 'opacity-60' : ''
              }`}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${getSeverityColor(alert.severity)}`}>
                        {getSeverityIcon(alert.severity)}
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">{alert.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span>{parseUtc(alert.timestamp).toLocaleString()}</span>
                          <Badge className={alert.status === 'resolved' ? 'bg-green-100 text-green-800' : getSeverityColor(alert.severity)}>
                            {alert.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Alert Distribution by Severity */}
            <Card>
              <CardHeader>
                <CardTitle>Alert Distribution by Severity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Critical', value: alertStats.critical || 0, fill: CHART_COLORS[0] },
                          { name: 'Warning', value: alertStats.warning || 0, fill: CHART_COLORS[1] },
                          { name: 'Info', value: alertStats.info || 0, fill: CHART_COLORS[2] }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {alerts.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Alert Trends Over Time */}
            <Card>
              <CardHeader>
                <CardTitle>Alert Trends (24h)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={alertHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestamp" 
                        tickFormatter={(value) => new Date(value).toLocaleTimeString()}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => new Date(value).toLocaleString()}
                      />
                      <Line type="monotone" dataKey="alert_count" stroke={CHART_COLORS[0]} name="Total Alerts" />
                      <Line type="monotone" dataKey="cost_alerts" stroke={CHART_COLORS[1]} name="Cost Alerts" />
                      <Line type="monotone" dataKey="resource_alerts" stroke={CHART_COLORS[2]} name="Resource Alerts" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Alert Types Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Alert Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={
                      Object.entries(alertStats.by_type || {}).map(([type, count]) => ({ type, count }))
                    }>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="type" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill={CHART_COLORS[0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="muted" className="space-y-4">
          <div className="space-y-4">
            {Array.from(mutedAlerts).map((alertId) => {
              const alert = alerts.find(a => a.id === alertId);
              if (!alert) return null;
              
              return (
                <Card key={alert.id} className="opacity-60">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg ${getSeverityColor(alert.severity)}`}>
                          <BellOff className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">{alert.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => unmuteAlert(alert.id)}
                        variant="outline"
                        size="sm"
                        className="flex items-center space-x-2"
                      >
                        <Bell className="w-4 h-4" />
                        <span>Unmute</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {mutedAlerts.size === 0 && (
              <Card>
                <CardContent className="py-16">
                  <div className="text-center">
                    <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No Muted Alerts
                    </h3>
                    <p className="text-gray-600">
                      All alerts are currently active
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Last Updated */}
      <div className="text-xs text-gray-500 text-center">
        Last updated: {new Date().toLocaleString()} • Auto-refresh every 15 seconds
      </div>
    </div>
  );
};

export default RealTimeAlertsDashboard;